/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState, useCallback } from 'react';
import { Button, Form, Input, Row, Col, Card, CardBody } from 'reactstrap';
import { CustomForm } from 'components/Generic/CustomForm';
import { useForm } from 'react-hook-form';
import { Text } from 'components/Form/Fields';
import { useMemo } from 'react';
import { TableStockClient } from '../Client/table-stock-client';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { handleSendGeneric } from 'services/generic';
import { convertBrl } from 'utility/generic';
import { convertToFloatBr } from 'utility/generic';
import { Notification } from 'notification/notification';
import { convertToFloat } from 'utility/generic';
import { TableVisitRecord } from '../Client/table-visit-client';
import { ModalAdjustDebtor } from './modal-adjust-debtor';

const toRedirect = '/admin/sale/execute-sale/';

export const ClientSaleForm = () => {
  const location = useLocation();
  const history = useHistory();
  const { id } = useParams();
  const { reset, getValues, control, setValue } = useForm({
    defaultValues: {
      client: null,
      productList: [],
    },
  });
  const [stock, setStock] = useState([]);
  const [customClient, setCustomClient] = useState(0);
  const [historyKey, setHistoryKey] = useState(0);
  const [modalAdjustOpen, setModalAdjustOpen] = useState(false);

  // Normaliza dados de um produto garantindo que valores undefined sejam zerados
  const normalizeProductData = useCallback((product) => {
    return {
      ...product,
      amount_paid: product.amount_paid
        ? convertToFloat(product.amount_paid)
        : 0,
      sold: product.sold ? parseInt(product.sold, 10) : 0,
      returned: product.returned ? parseInt(product.returned, 10) : 0,
      value: product.value ? convertToFloat(product.value) : 0,
      debtor: product.debtor ? convertToFloat(product.debtor) : 0,
      quantity: product.quantity ? parseInt(product.quantity, 10) : 0,
    };
  }, []);

  // Processa todo formulário antes do envio
  // Adiciona timestamps para rastreamento de pagamentos offline
  // Normaliza todos os valores para garantir integridade dos dados
  const processFormData = useCallback(() => {
    const formData = getValues();

    return {
      ...formData,
      executedAt: new Date().toISOString(),
      timestamp: Date.now(),
      productList: (formData.productList || []).map((product) =>
        normalizeProductData(product),
      ),
      client: formData.client
        ? {
            ...formData.client,
            commission: parseFloat(formData.client.commission) || 0,
          }
        : null,
    };
  }, [getValues, normalizeProductData]);

  const columns = useMemo(
    () => [
      {
        Header: 'Produto',
        accessor: 'name',
      },
      {
        Header: 'Venda',
        accessor: 'quantity',
      },
      {
        Header: 'Vendas',
        accessor: 'sold',
        Cell: ({ row }) => {
          const [quantidade, setQuantidade] = useState(0);

          const onChange = (e) => {
            const value = e.target.value;
            const newQuantity = parseInt(value, 10);

            if (isNaN(newQuantity)) {
              setQuantidade('0');
            } else {
              setQuantidade(newQuantity);
              const returned = row.original.quantity - newQuantity;
              setStock((currentData) => {
                const nextData = [...currentData];

                const productIndex = nextData.findIndex(
                  (item) => item.id === row.original.id,
                );

                if (productIndex !== -1) {
                  nextData[productIndex].sold = newQuantity;
                  nextData[productIndex].returned = returned;
                  setValue('productList', nextData);
                }

                return nextData;
              });
            }
          };

          return (
            <Input
              className='form-control-alternative'
              id='input-first-name'
              size={'sm'}
              type='number'
              value={quantidade}
              onChange={onChange}
              style={{ fontSize: '18px' }}
            />
          );
        },
      },
      {
        Header: 'Receber',
        accessor: 'value_received',
        Cell: ({ row }) => {
          if (row.original && row.original?.sold && row.original?.value) {
            const value = convertToFloat(row.original.value);
            const toReceive = row.original.sold * value;
            const valueFinal =
              toReceive - toReceive * (getValues('client').commission / 100);
            return (
              <span style={{ fontSize: '18px' }}>{convertBrl(valueFinal)}</span>
            );
          }

          return <span style={{ fontSize: '18px' }}>{convertBrl(0)}</span>;
        },
      },
      {
        Header: 'Pago',
        accessor: 'debtor_paid',
        Cell: ({ row }) => {
          const [valor, setValor] = useState(0);
          const onChange = (e) => {
            const valueOnChange = e.target.value;
            if (Number(valueOnChange) < 0) {
              Notification(400, 'O valor deve ser maior ou igual a zero!');
            } else {
              setValor(valueOnChange);
              setStock((currentData) => {
                const nextData = [...currentData];

                const productIndex = nextData.findIndex(
                  (item) => item.id === row.original.id,
                );

                if (productIndex !== -1) {
                  nextData[productIndex].amount_paid =
                    convertBrl(valueOnChange);
                  setValue('productList', nextData);
                }

                return nextData;
              });
            }
          };

          return (
            <Input
              className='form-control-alternative'
              id='input-first-name'
              size={'sm'}
              type='text'
              value={valor}
              onChange={onChange}
              style={{
                fontSize: '18px',
                width: '120px',
              }}
            />
          );
        },
      },
      {
        Header: 'Devedor',
        accessor: 'debtor_',
        Cell: ({ row }) => {
          const value = convertToFloat(row.original.value);
          const toReceive = row.original.returned * value;
          const commisionClient = getValues('client').commission / 100;
          const valueFinal = toReceive - toReceive * commisionClient;
          const debtor = convertToFloat(row.original.debtor);
          const amount_paid = convertToFloat(row.original.amount_paid);
          const debtor_final = debtor - amount_paid - valueFinal;
          return (
            <span style={{ fontSize: '18px' }}>{convertBrl(debtor_final)}</span>
          );
        },
      },
      {
        Header: 'Devolução',
        accessor: 'returned',
        Cell: ({ row }) => {
          const sold = row.original.sold;
          return (
            <span>
              {sold === 0 || sold === null ? 0 : row.original.returned}
            </span>
          );
        },
      },
    ],
    [setValue],
  );

  const calculateTotalDebtor = useCallback(() => {
    return stock.reduce((total, item) => {
      const value = convertToFloat(item.value);
      const toReceive = item.returned * value;
      const commisionClient = getValues('client')?.commission / 100 || 0;
      const valueFinal = toReceive - toReceive * commisionClient;
      const debtor = convertToFloat(item.debtor);
      const amount_paid = convertToFloat(item.amount_paid || 0);
      const debtor_final = debtor - amount_paid - valueFinal;
      return total + debtor_final;
    }, 0);
  }, [stock, getValues]);

  const DebtorSummary = () => {
    return (
      <Card d style={{ marginTop: '20px' }}>
        <CardBody>
          <Row>
            <Col md='6'>
              <h4 style={{ margin: 0, fontSize: '16px' }}>
                Saldo Devedor Total:
              </h4>
            </Col>
            <Col md='6' style={{ textAlign: 'right' }}>
              <h4
                style={{
                  margin: 0,
                  fontWeight: 'bold',
                  fontSize: '16px',
                  color: '#fb6340',
                }}
              >
                {convertBrl(calculateTotalDebtor())}
              </h4>
            </Col>
          </Row>
        </CardBody>
      </Card>
    );
  };

  useEffect(() => {
    const { state } = location;
    if (state === undefined) {
      getAllProducts(id).then((response) => {
        setValue('client', response);
        setCustomClient(response);
      });
    } else if (state.client === undefined) {
      getAllProducts(state.id).then((response) => {
        setValue('client', response);
        setCustomClient(response);
      });
    } else {
      getAllProducts(state.client.id).then((response) => {
        setValue('client', response);
        setCustomClient(response);
      });
    }

    async function getAllProducts(id) {
      try {
        const data = await handleSendGeneric('/v1/sale/execute/' + id, 'GET');
        setStock(data.stocks);
        return data;
      } catch (error) {
        setStock([]);
        return undefined;
      }
    }
  }, [location, setValue, setStock, id]);

  return (
    <>
      <CustomForm
        nameForm={'Criar Venda'}
        typeForm={'POST'}
        pathUrl={'/v1/sale/execute'}
        toRedirect={toRedirect}
        getValues={processFormData}
        loadValues={reset}
        forceRefreshPage
        onlyReturnPage
      >
        <Form>
          <div className='pl-lg-4'>
            <Row>
              <Text
                control={control}
                label={'Razão Social'}
                fieldName={'client.name'}
                placeholder={'Informe a razão social'}
                size={4}
                readOnly={true}
              />
              <Text
                control={control}
                fieldName={'client.commission'}
                label={'Comissão'}
                placeholder={'Informe a comissão do cliente'}
                size={2}
                readOnly={true}
              />
              <Text
                control={control}
                label={'Cidade'}
                placeholder={''}
                fieldName={'client.city'}
                size={4}
                readOnly={true}
              />
              {/* <StatusClient status={watch('stateClientEnum')} size={2} /> */}
            </Row>

            <div
              style={{
                paddingTop: '2%',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '12px',
                }}
              >
                <div
                  style={{ display: 'flex', gap: '12px', alignItems: 'center' }}
                >
                  <Button
                    type='button'
                    color='info'
                    size='sm'
                    onClick={() =>
                      history.push(
                        '/admin/sale/pre-sale/new',
                        getValues('client'),
                      )
                    }
                    style={{ width: '120px', height: '60px', fontSize: '14px' }}
                  >
                    Pré Venda
                  </Button>
                </div>
                {stock.length > 0 && <DebtorSummary />}
              </div>
              {stock.length > 0 && (
                <TableStockClient
                  columns={columns}
                  data={stock.filter(
                    (stock) =>
                      convertToFloatBr(stock.debtor) > 0 ||
                      (stock.type === 'GIFT' && stock.quantity > 0),
                  )}
                />
              )}
            </div>

            <div style={{ marginTop: '24px' }}>
              <h3 style={{ marginTop: '20px', fontSize: '18px' }}>Histórico</h3>
              {customClient?.id && (
                <TableVisitRecord key={historyKey} id={customClient?.id} />
              )}
            </div>
          </div>
        </Form>
      </CustomForm>

      {customClient?.id && (
        <ModalAdjustDebtor
          isOpen={modalAdjustOpen}
          onToggle={() => setModalAdjustOpen(false)}
          clientId={customClient.id}
          saldoAtual={stock.reduce(
            (acc, s) => acc + (convertToFloat(s.debtor) || 0),
            0,
          )}
          onSuccess={() => {
            setHistoryKey((k) => k + 1);
          }}
        />
      )}
    </>
  );
};
